import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { IconCopy, IconRefresh, IconWebhook } from '@tabler/icons-react';
import { Button, Card, Flex, Input, Modal, notification, Space, Spin, Table, Tag, Tooltip } from 'antd';

import Webhook from './Webhook';
import Account from '../Account';
import router from '~/configs/routes';
import { serviceCopyKeyBoard } from '~/configs';
import { logoutUserSuccess } from '~/redux/reducer/auth';
import { requestUserGetApikey, requestUserUpdateApikey } from '~/services/account';

function ApiKey() {
    const [loading, setLoading] = useState(false);
    const [serviceKeys, setServiceKeys] = useState([]);
    const [whiteListIp, setWhitelistIp] = useState('');
    const [openRefresh, setOpenRefresh] = useState(false);
    const [openWhitelist, setOpenWhitelist] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [selectedApiKey, setSelectedApiKey] = useState(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = 'Thegioicode.com - Apikey Dịch vụ đang sử dụng';

        const fetch = async () => {
            setLoading(true);
            const result = await requestUserGetApikey();

            setLoading(false);
            if (result.status === 401 || result.status === 403) {
                dispatch(logoutUserSuccess());
                navigate(router.home);
            } else if (result?.status === 200) {
                setServiceKeys(result.data);
            } else {
                notification.error({
                    message: 'Thông báo',
                    description: result?.error || 'Lỗi hệ thống vui lòng thử lại sau',
                });
            }
        };
        fetch();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onExpand = (record) => {
        const keys = expandedRowKeys.includes(record.key)
            ? expandedRowKeys.filter((k) => k !== record.key)
            : [...expandedRowKeys, record.key];
        setExpandedRowKeys(keys);
    };

    const defaultExpandable = {
        expandedRowRender: (record) => <Webhook webhooks={record.webhooks} api_key={record.api_key} />,
        expandedRowKeys,
        onExpand: (_, record) => onExpand(record),
        expandIcon: () => null,
    };

    const handleRefreshApikey = async () => {
        if (!selectedApiKey || !selectedApiKey.id) {
            return notification.error({
                message: 'Thông báo',
                description: 'Vui lòng chọn apikey cần làm mới',
            });
        }

        const data = { id: selectedApiKey.id };

        const result = await requestUserUpdateApikey(data, 'refresh');

        if (result.status === 401 || result.status === 403) {
            dispatch(logoutUserSuccess());
            navigate(router.home);
        } else if (result?.status === 200) {
            const cloneServiceKeys = [...serviceKeys];

            const apikeyIndex = cloneServiceKeys.findIndex((apikey) => apikey.id === selectedApiKey.id);
            if (apikeyIndex === -1) {
                return notification.error({
                    message: 'Thông báo',
                    description: 'Apikey dịch vụ không tồn tại',
                });
            }

            cloneServiceKeys[apikeyIndex].api_key = result.data;
            cloneServiceKeys[apikeyIndex].updated_at = Date.now();
            setServiceKeys(cloneServiceKeys);

            setOpenRefresh(false);
            notification.success({
                message: 'Thông báo',
                description: result.message,
            });
        } else {
            notification.error({
                message: 'Thông báo',
                description: result.error || 'Lỗi hệ thống vui lòng thử lại sau',
            });
        }
    };

    const handleWhitelistIp = async () => {
        if (!selectedApiKey || !selectedApiKey.id) {
            return notification.error({
                message: 'Thông báo',
                description: 'Vui lòng chọn apikey whitelist IP',
            });
        }

        const data = {
            id: selectedApiKey.id,
            whitelist_ip: whiteListIp
                ? whiteListIp
                      .split(',')
                      .map((ip) => ip.trim())
                      .filter((ip) => ip !== '')
                : [],
        };

        const result = await requestUserUpdateApikey(data, 'whitelist');

        if (result.status === 401 || result.status === 403) {
            dispatch(logoutUserSuccess());
            navigate(router.home);
        } else if (result?.status === 200) {
            const cloneServiceKeys = [...serviceKeys];

            const apikeyIndex = cloneServiceKeys.findIndex((apikey) => apikey.id === selectedApiKey.id);
            if (apikeyIndex === -1) {
                return notification.error({
                    message: 'Thông báo',
                    description: 'Apikey dịch vụ không tồn tại',
                });
            }

            cloneServiceKeys[apikeyIndex].whitelist_ip = result.data;
            cloneServiceKeys[apikeyIndex].updated_at = Date.now();
            setServiceKeys(cloneServiceKeys);

            setOpenWhitelist(false);
            notification.success({
                message: 'Thông báo',
                description: result.message,
            });
        } else {
            notification.error({
                message: 'Thông báo',
                description: result.error || 'Lỗi hệ thống vui lòng thử lại sau',
            });
        }
    };

    const columns = [
        {
            title: 'Dịch vụ',
            dataIndex: 'category',
            key: 'category',
            render: (category) => <div style={{ whiteSpace: 'nowrap' }}>{category}</div>,
        },
        {
            title: 'Key',
            dataIndex: 'api_key',
            key: 'api_key',
            render: (api_key) => (
                <Flex align="center">
                    <span className="mr-2">{api_key}</span>
                    <Tooltip title="Sao chép">
                        <IconCopy className="cursor-pointer" size={18} stroke={1.5} onClick={() => serviceCopyKeyBoard(api_key)} />
                    </Tooltip>
                </Flex>
            ),
        },
        {
            title: 'Lượt dùng',
            key: 'used',
            render: (data) => (
                <Fragment>
                    <Tooltip title="Lượt dùng miễn phí">
                        <span className="text-success">{data.free_usage}</span>
                    </Tooltip>
                    <span className="mx-1">/</span>
                    <Tooltip title="Tổng lượt dùng">
                        <span className="text-info">{data.used}</span>
                    </Tooltip>
                </Fragment>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'green';
                let message = 'Hoạt động';

                if (!status) {
                    color = 'red';
                    message = 'Tạm khoá';
                }

                return <Tag color={color}>{message}</Tag>;
            },
        },
        {
            title: 'Ngày tạo/cập nhật',
            key: 'date',
            render: (data) => (
                <Fragment>
                    <span>{moment(data.created_at).format('DD/MM/YYYY HH:mm:ss')}</span>
                    <br />
                    <span>{moment(data.updated_at).format('DD/MM/YYYY HH:mm:ss')}</span>
                </Fragment>
            ),
        },
        {
            title: 'Hành động',
            key: 'action',
            render: (data, record) => (
                <Space>
                    <Tooltip title="Làm mới apikey">
                        <Button
                            type="primary"
                            size="small"
                            className="box-center"
                            onClick={() => {
                                setSelectedApiKey(data);
                                setOpenRefresh(true);
                            }}
                        >
                            <IconRefresh size={18} />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Xem webhook">
                        <Button type="primary" size="small" className="box-center" onClick={() => record.onExpand(record)}>
                            <IconWebhook size={18} />
                        </Button>
                    </Tooltip>
                    <Button
                        type="primary"
                        size="small"
                        className="box-center"
                        onClick={() => {
                            setSelectedApiKey(data);
                            setOpenWhitelist(true);
                            setWhitelistIp(data.whitelist_ip.join(', ') || '');
                        }}
                    >
                        Whitelist IP
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Account>
            <div className="w-full">
                <h3 className="font-bold font-size-20 border-bottom pb-2 mb-5">Danh sách apikey</h3>

                {loading ? (
                    <Flex align="center" justify="center" style={{ minHeight: '40vh' }}>
                        <Spin />
                    </Flex>
                ) : (
                    <Card>
                        <Table
                            columns={columns}
                            dataSource={serviceKeys.map((serviceKey) => ({ key: serviceKey.id, ...serviceKey, onExpand }))}
                            pagination={false}
                            expandable={defaultExpandable}
                        />
                    </Card>
                )}
            </div>

            <Modal
                centered
                closable={false}
                maskClosable={false}
                open={openRefresh}
                onOk={() => handleRefreshApikey()}
                onCancel={() => setOpenRefresh(false)}
                width={400}
                okText="Xác nhận"
                cancelText="Hủy"
            >
                <h2 className="font-size-15">Bạn có chắc chắn muốn làm mới API key này?</h2>
            </Modal>

            <Modal
                centered
                closable={false}
                maskClosable={false}
                open={openWhitelist}
                onOk={() => handleWhitelistIp()}
                onCancel={() => setOpenWhitelist(false)}
                width={460}
                okText="Xác nhận"
                cancelText="Hủy"
            >
                <h2 className="font-size-15">Whitelist IP có thể dùng Apikey này!</h2>

                <div className="text-center py-4">
                    <Input
                        size="large"
                        placeholder="Mỗi IPv4 cách nhau một dấu phẩy (,)"
                        value={whiteListIp}
                        onChange={(e) => setWhitelistIp(e.target.value)}
                    />
                </div>
            </Modal>
        </Account>
    );
}

export default ApiKey;
